import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { DatePicker, Checkbox } from 'antd';
import dayjs from 'dayjs';

import { DATE_FORMAT, SLIDE_PERIOD_DEFAULT_END_DATE_OFFSET_DAYS } from '../constants';
import { PeriodPickerStyle } from './SlidePeriodPicker.style';
import { SlidePeriod } from '../types';

type SlidePeriodPickerProps = {
  value?: SlidePeriod;
  onChange?: (next: SlidePeriod) => void;
};

type SlidePeriodPickerRef = Pick<SlidePeriodPickerProps, 'value' | 'onChange'>;

// 빈 값 sentinel — props.value가 undefined일 때 컴포넌트 내부에서 기본 구조를 갖추기 위한 로컬 상수
const EMPTY_PERIOD: SlidePeriod = { startDate: '', endDate: null };

export const SlidePeriodPicker = forwardRef<SlidePeriodPickerRef, SlidePeriodPickerProps>(
  (props, ref) => {
    const { value = EMPTY_PERIOD, onChange } = props;
    const isInfinite = value.endDate === null;
    // 무기한 해제 시 직전 endDate를 복원하기 위해 마지막 지정값을 보존
    const lastEndDateRef = useRef<string | null>(value.endDate);

    useEffect(() => {
      if (value.endDate !== null) {
        lastEndDateRef.current = value.endDate;
      }
    }, [value.endDate]);

    useImperativeHandle(ref, () => ({ value, onChange }));

    const handleStartDateChange = (date: dayjs.Dayjs | null) => {
      if (!date) return;
      onChange?.({
        ...value,
        startDate: date.format(DATE_FORMAT),
      });
    };

    const handleEndDateChange = (date: dayjs.Dayjs | null) => {
      if (!date) return;
      onChange?.({
        ...value,
        endDate: date.format(DATE_FORMAT),
      });
    };

    const handleInfiniteToggle = (checked: boolean) => {
      if (checked) {
        onChange?.({ ...value, endDate: null });
        return;
      }
      const baseDate = value.startDate ? dayjs(value.startDate, DATE_FORMAT, true) : dayjs();
      const restoredEndDate =
        lastEndDateRef.current ?? baseDate.add(SLIDE_PERIOD_DEFAULT_END_DATE_OFFSET_DAYS, 'days').format(DATE_FORMAT);
      onChange?.({ ...value, endDate: restoredEndDate });
    };

    return (
      <PeriodPickerStyle>
        <div className="picker-row">
          <label className="picker-label">시작일</label>
          <DatePicker
            allowClear={false}
            showTime
            format={DATE_FORMAT}
            value={value.startDate ? dayjs(value.startDate, DATE_FORMAT, true) : null}
            onChange={handleStartDateChange}
            placeholder="시작일 선택"
          />
        </div>
        <div className="picker-row">
          <label className="picker-label">종료일</label>
          <DatePicker
            allowClear={false}
            showTime
            format={DATE_FORMAT}
            value={value.endDate ? dayjs(value.endDate, DATE_FORMAT, true) : null}
            onChange={handleEndDateChange}
            disabled={isInfinite}
            placeholder={isInfinite ? '무기한' : '종료일 선택'}
          />
        </div>
        <div className="checkbox-row">
          <Checkbox
            checked={isInfinite}
            onChange={(e) => handleInfiniteToggle(e.target.checked)}
          >
            종료일 없이 무기한 노출
          </Checkbox>
        </div>
      </PeriodPickerStyle>
    );
  },
);

SlidePeriodPicker.displayName = 'SlidePeriodPicker';
